import React, { useState, useMemo } from "react";
import footprint from "../../../../assets/footprint-fill.png";
import { RiPinDistanceFill, RiFireLine } from "react-icons/ri";
import "./GoalCompletionRate.css";

type MetricType = "steps" | "distance" | "caloriesburned";

interface GoalCompletionRateProps {
  data: {
    group_name: string;
    data: {
      date: string;
      metrics: {
        [key: string]: any;
      }[];
    }[];
  }[];
}

const GoalCompletionRate: React.FC<GoalCompletionRateProps> = ({ data }) => {
  const [selectedMetric, setSelectedMetric] = useState<MetricType>("steps");

  const getColorByGCR = (gcr: number): string => {
    const clamped = Math.min(100, Math.max(0, gcr));
    const startRGB = [202, 255, 253]; // light cyan
    const endRGB = [9, 71, 82];       // dark cyan

    const r = Math.round(startRGB[0] + (endRGB[0] - startRGB[0]) * (clamped / 100));
    const g = Math.round(startRGB[1] + (endRGB[1] - startRGB[1]) * (clamped / 100));
    const b = Math.round(startRGB[2] + (endRGB[2] - startRGB[2]) * (clamped / 100));

    return `rgb(${r}, ${g}, ${b})`;
  };

  // 1. Create a sorted master date list from all team data
  const allDates = useMemo(() => {
    const dateSet = new Set<string>();
    data.forEach(team => {
      team.data.forEach(day => {
        dateSet.add(day.date);
      });
    });
    return Array.from(dateSet).sort();
  }, [data]);

  const dateLabels = allDates.map(dateStr =>
    new Date(dateStr).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
    })
  );

  // 2. Map each team’s data aligned to master date list
  const heatmapRows = useMemo(() => {
    return data.map((team, tIdx) => {
      // Build a date → GCR map for quick lookup
      const gcrMap: Record<string, number> = {};
      team.data.forEach(day => {
        const gcr = day.metrics?.[0]?.[selectedMetric]?.gcr ?? 0;
        gcrMap[day.date] = gcr;
      });

      return (
        <div key={tIdx} className="heatmap-row">
          <div className="heatmap-label">{team.group_name}</div>
          {allDates.map((date, dIdx) => (
            <div
              key={dIdx}
              className="heatmap-cell"
              style={{ backgroundColor: getColorByGCR(gcrMap[date] ?? 0) }}
            />
          ))}
        </div>
      );
    });
  }, [data, selectedMetric, allDates]);

  return (
    <div className="goal-completion-container">
      <h5 className="goal-completion-title">Goal Completion Rate</h5>
  
      <div className="TeamCompMetricToggleContainer">
        <div className="TeamCompMetric-Icons">
          <button
            className={`GraphIcon ${selectedMetric === "steps" ? "active" : ""}`}
            onClick={() => setSelectedMetric("steps")}
          >
            <img src={footprint} alt="Steps" />
          </button>
          <button
            className={`GraphIcon ${selectedMetric === "distance" ? "active" : ""}`}
            onClick={() => setSelectedMetric("distance")}
          >
            <RiPinDistanceFill />
          </button>
          <button
            className={`GraphIcon ${selectedMetric === "caloriesburned" ? "active" : ""}`}
            onClick={() => setSelectedMetric("caloriesburned")}
          >
            <RiFireLine />
          </button>
        </div>
      </div>
  
      <div className="heatmap-scroll">
        <div className="heatmap-table">
          {/* Header Row */}
          <div className="heatmap-row heatmap-header-row">
            <div className="heatmap-label">Group</div>
            {dateLabels.map((label, idx) => (
              <div key={idx} className="heatmap-header">{label}</div>
            ))}
          </div>
  
          {/* Heatmap Data Rows */}
          {heatmapRows}
        </div>
      </div>
  
      {/* Legend */}
      <div className="heatmap-legend">
        <span>0%</span>
        <div className="legend-bar" />
        <span>100%</span>
      </div>
  
      {/* Metric Label Below */}
      <div className="metric-label-bottom">
        {selectedMetric.charAt(0).toUpperCase() + selectedMetric.slice(1)}
      </div>
    </div>
  );  
};

export default GoalCompletionRate;
